/**
 * One day of the Planner's calendars (spec 2026-10-14 §9): a 34pt disc with the date, an optional
 * weekday initial above (the week strip), and one dot below while work is left that day — never on
 * the picked day, whose list is right there. Colours come from dayLook. On web an unfilled disc tints
 * on hover. The whole column is the 44pt press target and keeps the label both calendars share
 * (formatDayAccessibilityLabel).
 */
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { formatDayAccessibilityLabel, toDateKey } from '@/utils/residents/todoCalendar';
import { dayLook } from './dayLook';

/** The disc: 15pt digits at the 1.2x micro cap (18pt) still sit inside it. */
export const DAY_DISC = 34;
/** The dot under a day with work left. */
const DOT = 4;

export interface DayCellProps {
  day: Date;
  isToday: boolean;
  isSelected: boolean;
  /** A padding day from the previous or next month (the month grid). */
  isOutside?: boolean;
  /** Remaining to-dos that day: one dot while there are any. */
  count: number;
  /** The week strip's initial above the disc ("S", "M", …). */
  weekdayInitial?: string;
  onPress: (day: Date) => void;
}

export const DayCell: React.FC<DayCellProps> = React.memo(
  ({ day, isToday, isSelected, isOutside = false, count, weekdayInitial, onPress }) => {
    const theme = useThemeColors();
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const styles = useThemedStyles(() =>
      StyleSheet.create({
        cell: {
          flex: 1,
          minHeight: 44,
          alignItems: 'center',
          gap: space.xs,
        },
        initial: {
          ...type.captionMedium,
        },
        disc: {
          width: DAY_DISC,
          height: DAY_DISC,
          borderRadius: radius.full,
          alignItems: 'center',
          justifyContent: 'center',
        },
        number: {
          ...type.subhead,
        },
        dot: {
          width: DOT,
          height: DOT,
          borderRadius: radius.full,
        },
      }),
    );
    const look = dayLook({ isToday, isSelected, isOutside }, theme);
    const key = toDateKey(day);
    const showDot = count > 0 && !isSelected;
    // The dot's slot is always laid out, so a cell keeps its height; it only paints while work is left.
    const dotInk = showDot ? look.dotInk : 'transparent';
    return (
      <Hoverable
        hover="none"
        style={styles.cell}
        onPress={() => onPress(day)}
        accessibilityRole="button"
        accessibilityState={{ selected: isSelected }}
        accessibilityLabel={formatDayAccessibilityLabel(day, count)}
      >
        {({ hovered }) => (
          <>
            {weekdayInitial ? (
              <Text scale="micro" style={[styles.initial, { color: look.initialInk }]}>
                {weekdayInitial}
              </Text>
            ) : null}
            <View
              testID={`day-disc-${key}`}
              style={[
                styles.disc,
                {
                  backgroundColor:
                    look.fill ?? (hovered ? theme.semantic.background.hover : 'transparent'),
                },
              ]}
            >
              <Text scale="micro" style={[styles.number, { color: look.ink, fontWeight: look.weight }]}>
                {String(day.getDate())}
              </Text>
            </View>
            <View
              testID={showDot ? `todo-dot-${key}` : undefined}
              style={[styles.dot, { backgroundColor: dotInk }]}
            />
          </>
        )}
      </Hoverable>
    );
  },
);

DayCell.displayName = 'DayCell';
